import { useCallback, useEffect, useState, useRef } from 'react';
import cardInsertImage from '../../assets/TapSwipeInsert.png';
import style from './style.module.scss';
import { handleCardDeviceMessage } from './handleCardDeviceMessage';
import MessageBoxSet from '../message-box-set';
import { useStore } from '../../lib/hooks/useStore';
import { ViewState, PaymentError } from '../../lib/types/paymentCardState';
import Confirm from '../confirm';
import MainMessageBox from '../../features/main-message-box/main-message-box';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import logger from '../../lib/helpers/logger';
import BalancesKvpList from '../balances-kvp-list';
import { PaymentCardServerProps } from '../../lib/typegen/payment-card-server-props';
import api from '../../lib/api/api';
import { v4 as guid } from 'uuid';
import EMVDevice, { KioEMVTransactionParams, KioEMVResult, TransactionStates } from '../../lib/devices/emv-device/EMVDevice';
import MessageBox from '../message-box';
import { useSessionCleanup } from '../../lib/hooks/useSessionCleanup';
import ServiceFeeView from '../service-fee-view';
import { LegacyPostAcceptedPayment, LegacyPostRejectedPayment } from '../../lib/types/legacyAdminApi';
import passKeyHelper from '../../lib/helpers/passKey';
import enumHelper from '../../lib/helpers/enums';
import { PaymentFeeViewServerProps } from '../../lib/typegen/payment-fee-view-server-props';
import { PaymentType } from '../../lib/typegen/payment-type';
import { getPPBehavior } from '../../lib/integrations';
import { PayWithTokenRequest } from '../../lib/typegen/pay-with-token-request';
import { AppEnvironment } from '../../lib/typegen/app-environment';
import { toTranslatorLanguageCode } from '../../lib/helpers/translatorLanguageCode';

function PaymentCardToken({
	imageAltKey,
	paymentFeeViewServerProps,
	paymentTotalLabelKey,
	warningBoxLabelKey,
	cardErrorGenericLabelKey,
	tryAgainMessageLabelKey,
	tryAgainNoLabelKey,
	tryAgainYesLabelKey,
}: PaymentCardServerProps) {
	const [currentState, setCurrentState] = useState<ViewState>(ViewState.SERVICE_FEE);
	const [error, setError] = useState<PaymentError | null>(null);
	const [errorHint, setErrorHint] = useState<string | null>(null);
	const emvDeviceRef = useRef<EMVDevice | null>(null);
	const hasConfirmedFeeRef = useRef(false);
	const isPaymentSentToServerRef = useRef(false);
	const isCancellingRef = useRef(false);
	const [deviceMessage, setDeviceMessage] = useState<string>('');
	const PAYMENT_CARD_TOKEN_REGISTRY_KEY = 'payment-card-token';
	// Test token used to drive the simulated (mock device) token flow through the real authorization path.
	// Must match the server's FiservTestToken (FiservGatewayApi.cs) so it injects the dummy expiry the
	// Fiserv auth requires in test environments; otherwise the auth is declined with "Non-numeric expiry".
	const SIMULATED_TOKEN = '4788250000121443';

	const {
		accountStore: {
			cartItems,
			cartTotalAmount,
			transactionFee,
			setTransactionFee,
			transactionTotalWithFee,
			setTransactionTotalWithFee,
			payorInformation,
			cardType,
			setPaymentType,
		},
		sessionStore: { legacyId },
		kioskStore: { kioskId, companyId, paymentReferenceId },
		uiStore: { handleNavigation, setCustomBackAction, clearCustomBackAction, showLoader, hideLoader, setCanCancel },
		configStore,
		configStore: { getString, kioskLocation, cisType, ppType, environment },
	} = useStore();

	const ppBehavior = getPPBehavior(ppType);

	// Translate a device message at runtime, auto-translating unknown strings on demand
	const translateDeviceMessage = useCallback(
		async (msg: string): Promise<string> => {
			const translated = getString(msg);
			if (translated !== msg || configStore.currentLanguage === 'en') return translated;

			try {
				const result = await configStore.autoTranslate({ [msg]: msg }, toTranslatorLanguageCode(configStore.currentLanguage));
				const value = result?.[msg];
				if (value) {
					configStore.setTranslation(configStore.currentLanguage, msg, value);
					return value;
				}
			} catch (err) {
				logger.logGeneral.error({}, 'Failed to auto-translate device message', err);
			}
			return msg;
		},
		[getString, configStore]
	);

	const shouldSimulate = (() => {
		if (!window.KioEMVDevice) return true;
		if (environment === AppEnvironment.Prod || environment === AppEnvironment.QA) return false;
		if (environment === AppEnvironment.Demo) return true;
		if (environment === AppEnvironment.Dev) {
			try {
				return window.KioDevice?.GetStatus?.('EMV') !== 0;
			} catch {
				return true;
			}
		}
		return true;
	})();

	useEffect(() => setPaymentType(cardType === 1 ? PaymentType.DebitCard : PaymentType.CreditCard), [setPaymentType, cardType]);

	// Register card device callbacks
	useEffect(() => {
		if (shouldSimulate) return;

		const emvDevice = new EMVDevice('EMV');
		emvDeviceRef.current = emvDevice;

		emvDevice.setOnVP5300Msg((devName, msg) => {
			void handleCardDeviceMessage(ppBehavior, devName, msg, {
				log: (text) => logger.logCardReader.log({}, text),
				translate: translateDeviceMessage,
				show: setDeviceMessage,
			});
		});

		emvDevice.setOnStateChanged((newState) => {
			logger.logCardReader.log({}, `${ppBehavior.cardDeviceDisplayName} state changed: ${newState}`);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Process the token after extraction — authorize via API
	const processTokenPayment = useCallback(
		async (token: string, result: KioEMVResult) => {
			showLoader();

			const item = cartItems[0];
			if (!item.customer) item.customer = { customerId: '', name: `${payorInformation?.firstName} ${payorInformation?.lastName}` };

			const feeValue = transactionFee ? parseFloat(transactionFee) : 0;
			const safeFee = isNaN(feeValue) ? 0 : feeValue;

			const tokenRequest: PayWithTokenRequest = {
				token,
				customerName: item.customer!.name!,
				utilityAccountNumber: item.item.itemNumber,
				amountDue: item.amountToPay,
				amountToPay: item.amountToPay,
				serviceFee: safeFee > 0 ? safeFee : undefined,
				creditCardTypeId: cardType ?? undefined,
				group: item.item.group ?? undefined,
				classification: item.item.classification ?? undefined,
			};

			const legacyPostAcceptedPayment: LegacyPostAcceptedPayment = structuredClone({
				dynatouchGUID: legacyId!,
				firstCashTransaction: false,
				customerID: companyId.toString(),
				kioskID: kioskId.toString(),
				paymentGatewayExternalID: item.item.itemNumber,
				paymentDateTime: new Date().toISOString(),
				paymentAmount: cartTotalAmount,
				paymentType: 'Card',
				paymentGUID: guid(),
				passKey: await passKeyHelper.generatePassKey(),
				cashOnes: 0,
				cashTwos: 0,
				cashFives: 0,
				cashTens: 0,
				cashTwenties: 0,
				cashFifties: 0,
				cashHundreds: 0,
				cisProvider: enumHelper.getCisConfigTypeLabel(cisType),
				paymentGateway: enumHelper.getPpConfigTypeLabel(ppType),
				cisProviderExternalID: item.id!,
				payorName: item.customer!.name!,
				paymentReference: paymentReferenceId,
			});

			const legacyPostRejectedPayment: LegacyPostRejectedPayment = structuredClone({
				customerID: companyId.toString(),
				kioskID: kioskId.toString(),
				billpaySessionGUID: legacyId!,
				rejectedECID: '',
				rejectedReason: 'Card payment error',
				rejectedPaymentType: 'Card',
				paymentAmount: cartTotalAmount,
				payorName: item.customer!.name!,
				paymentDateTime: new Date().toISOString(),
				passKey: await passKeyHelper.generatePassKey(),
			});

			try {
				isPaymentSentToServerRef.current = true;
				setCanCancel(false);
				const response = await api.payments.token(tokenRequest, legacyPostAcceptedPayment, legacyPostRejectedPayment);

				logger.logGeneral.log({}, 'Token payment successful', {
					referenceId: result.ReferenceID,
					cardType: result.CardType,
					paymentId: response?.paymentId,
				});
				hideLoader();
				handleNavigation('payment-complete');
			} catch (err) {
				logger.logGeneral.error({}, 'Token payment API call failed:', err);
				hideLoader();
				setCanCancel(true);
				setError({ message: getString(cardErrorGenericLabelKey) || 'Payment authorization failed' });
				setErrorHint(getString('card-read-hint'));
				setCurrentState(ViewState.ERROR);
			}
		},
		[
			cartItems,
			payorInformation,
			transactionFee,
			cardType,
			legacyId,
			companyId,
			kioskId,
			paymentReferenceId,
			cartTotalAmount,
			cisType,
			ppType,
			getString,
			cardErrorGenericLabelKey,
			handleNavigation,
			setCanCancel,
			showLoader,
			hideLoader,
		]
	);

	// Handle the EMV transaction completed callback — extract token, then authorize
	const onTokenizationComplete = useCallback(
		async (result: KioEMVResult) => {
			// If the user cancelled via back button, ignore the cancel-triggered result
			if (isCancellingRef.current) {
				logger.logCardReader.log({}, 'Tokenization result ignored — user-initiated cancel in progress');
				return;
			}

			logger.logCardReader.log({}, 'Tokenization completed', JSON.stringify({ transType: result.TransType, resultCode: result.Result }));

			// Result 1 = Approved (tokenization success)
			if (result.Result === 1) {
				const tokenResult = EMVDevice.extractVP5300Token(result);
				if (tokenResult?.cardToken) {
					logger.logGeneral.log({}, 'Token extracted successfully', {
						token: tokenResult.cardToken,
						processorMsg: tokenResult.processorMsg,
					});
					await processTokenPayment(tokenResult.cardToken, result);
				} else {
					logger.logGeneral.error({}, 'Failed to extract token from VP5300 result', result);
					setError({ message: getString(cardErrorGenericLabelKey) || 'Failed to read card data' });
					setCurrentState(ViewState.ERROR);
				}
			} else {
				logger.logGeneral.error({}, 'Tokenization failed', { errors: result.Errors, errorCode: result.ErrorCode });
				setError({ message: result.Errors || getString(cardErrorGenericLabelKey) || 'Card read failed' });
				setErrorHint(getString('card-read-hint'));
				setCurrentState(ViewState.ERROR);
			}
		},
		[processTokenPayment, getString, cardErrorGenericLabelKey]
	);

	// Start the tokenization on the card device
	const startTokenization = useCallback(() => {
		if (!emvDeviceRef.current) {
			logger.logGeneral.error({}, `${ppBehavior.cardDeviceDisplayName} not initialized`, 'startTokenization');
			setError({ message: getString(cardErrorGenericLabelKey) || 'Device not ready' });
			setCurrentState(ViewState.ERROR);
			return;
		}

		const params: KioEMVTransactionParams = {
			PaymentAmount: Math.round(cartTotalAmount * 100),
		};

		const started = emvDeviceRef.current.runTokenization(params, onTokenizationComplete);
		if (!started) {
			logger.logGeneral.error({}, `Failed to start ${ppBehavior.cardDeviceDisplayName} tokenization`, 'startTokenization');
			setError({ message: getString(cardErrorGenericLabelKey) || 'Failed to start card reader' });
			setCurrentState(ViewState.ERROR);
		}
	}, [cartTotalAmount, onTokenizationComplete, getString, cardErrorGenericLabelKey, ppBehavior.cardDeviceDisplayName]);

	useSessionCleanup(
		PAYMENT_CARD_TOKEN_REGISTRY_KEY,
		useCallback(async () => {
			logger.logGeneral.warn({}, 'Cleaning up incomplete token payment session');
			try {
				emvDeviceRef.current?.cancelTransaction();
			} catch (err) {
				logger.debug.error('Error cancelling EMV Device during cleanup', err);
			}
		}, [])
	);

	// Initialize payment on mount
	useEffect(() => {
		showLoader();

		const initializePayment = async () => {
			if (shouldSimulate) {
				logger.logGeneral.log({}, '[PaymentCardToken] Test mode: Using simulated payment flow');
			}

			if (ppBehavior.requiresPayorInformation && !payorInformation) {
				logger.logGeneral.error({}, 'Payor information is missing', 'initializePayment');
				setError({ message: getString(cardErrorGenericLabelKey) || 'Payment information missing' });
				setCurrentState(ViewState.ERROR);
				hideLoader();
				return;
			}

			try {
				const ctx = { cartItems, kioskLocation, cartTotalAmount, payorInformation };
				let resolved: { fee: string; total: string };

				if (ppBehavior.requiresPrepareTransaction && ppBehavior.buildPrepareCardRequest) {
					logger.logGeneral.log({}, '[PaymentCardToken] Fee resolution: prepareTransaction branch', { pp: ppBehavior.label });
					const prepareRequest = ppBehavior.buildPrepareCardRequest(ctx);
					const prepareResponse = await api.payments.prepareTransaction(prepareRequest);
					resolved = ppBehavior.resolveFeesFromPrepareResponse?.(prepareResponse!, cardType ?? 0) ?? {
						fee: '0',
						total: cartTotalAmount.toFixed(2),
					};
				} else {
					logger.logGeneral.log({}, '[PaymentCardToken] Fee resolution: resolveCardFee branch', {
						pp: ppBehavior.label,
						hasResolveCardFee: !!ppBehavior.resolveCardFee,
					});
					resolved = ppBehavior.resolveCardFee?.(ctx, cardType ?? 0) ?? { fee: '0', total: cartTotalAmount.toFixed(2) };
				}

				logger.logGeneral.log({}, '[PaymentCardToken] Fee resolved', {
					fee: resolved.fee,
					total: resolved.total,
					cartTotalAmount,
					cardType,
				});

				setTransactionFee(resolved.fee);
				setTransactionTotalWithFee(resolved.total);

				const feeVal = parseFloat(resolved.fee);
				const nextState = feeVal === 0 || isNaN(feeVal) ? ViewState.PAY : ViewState.SERVICE_FEE;
				logger.logGeneral.log({}, `[PaymentCardToken] Transitioning to ${nextState}`, { feeVal });

				if (nextState === ViewState.PAY) {
					setCurrentState(ViewState.PAY);
				} else {
					setCurrentState(ViewState.SERVICE_FEE);
				}
			} catch (err) {
				logger.logGeneral.error({}, 'Failed to initialize token payment:', err);
				setError({ message: getString(cardErrorGenericLabelKey) || 'Failed to initialize payment' });
				setCurrentState(ViewState.ERROR);
			}

			hideLoader();
		};

		initializePayment();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Start tokenization when entering PAY state
	useEffect(() => {
		if (currentState === ViewState.PAY && !error && !shouldSimulate) {
			const timer = setTimeout(() => {
				startTokenization();
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [currentState, error, startTokenization, shouldSimulate]);

	// The simulated authorization path uses the Fiserv test token, which the server only honors
	// (injecting the required dummy expiry) in test environments — Dev/Demo/Dynamic. Mirrors FiservGatewayApi.IsTestEnvironment.
	const canSimulateAuth =
		shouldSimulate && (environment === AppEnvironment.Dev || environment === AppEnvironment.Demo || environment === AppEnvironment.Dynamic);

	// Simulate payment flow in non-production environments without real EMV hardware.
	// Routes through the real authorization path (api.payments.token) so the simulated/mock
	// device behaves like the real device — including attaching kioskPaymentId to the legacy post.
	useEffect(() => {
		if (!canSimulateAuth || currentState !== ViewState.PAY) return;

		const payTimer = setTimeout(async () => {
			logger.logGeneral.log({}, '[PaymentCardToken] Test mode: Starting simulated token authorization');

			const simulatedResult = {
				TransType: 0,
				Result: 1,
				Amount: cartTotalAmount.toFixed(2),
				Tax: '0',
				ReferenceID: guid(),
				AuthorizationCode: 'SIMULATED',
				Errors: '',
				ErrorCode: '',
				ExpiryDate: '',
				PrimaryAccountNumber: '',
				CardHashToken: SIMULATED_TOKEN,
				AcqRefData: '',
				ProcessData: '',
				ReceiptData: [],
				CardReference: '',
				RawData: {},
				CardType: cardType === 1 ? 'Debit' : 'Credit',
			} as KioEMVResult;

			await processTokenPayment(SIMULATED_TOKEN, simulatedResult);
		}, 3000);

		return () => {
			clearTimeout(payTimer);
			hideLoader();
		};
	}, [canSimulateAuth, currentState, processTokenPayment, cartTotalAmount, cardType, hideLoader]);

	// Poll the EMV device until it returns to Ready or timeout expires
	const pollForDeviceReady = useCallback((timeoutMs: number): Promise<boolean> => {
		return new Promise((resolve) => {
			const start = Date.now();
			const interval = setInterval(() => {
				try {
					if (emvDeviceRef.current?.getState() === TransactionStates.Ready) {
						clearInterval(interval);
						resolve(true);
						return;
					}
				} catch {
					/* device may not be available */
				}

				if (Date.now() - start >= timeoutMs) {
					clearInterval(interval);
					logger.logCardReader.warn({}, `Device did not return to Ready within ${timeoutMs}ms`);
					resolve(false);
				}
			}, 500);
		});
	}, []);

	// Handle back navigation with graceful EMV cleanup
	const handleBack = useCallback(async () => {
		// Scenario 3: Payment already sent to server — ignore back press
		if (isPaymentSentToServerRef.current) {
			logger.logGeneral.log({}, 'Back pressed while payment is being processed by server — ignoring');
			return;
		}

		// Scenario 1: Device is Ready — navigate back immediately
		try {
			if (emvDeviceRef.current?.getState() === TransactionStates.Ready) {
				handleNavigation('payment-method');
				return;
			}
		} catch {
			/* device may not be initialized */
		}

		// Scenario 2: Device not ready, transaction not sent to server — cancel and wait
		isCancellingRef.current = true;
		showLoader();

		try {
			logger.logCardReader.log({}, 'Cancelling EMV transaction for back navigation');
			emvDeviceRef.current?.cancelTransaction();
		} catch (err) {
			logger.logCardReader.error({}, 'Failed to cancel EMV transaction', err);
		}

		await pollForDeviceReady(30_000);

		hideLoader();
		isCancellingRef.current = false;
		handleNavigation('payment-method');
	}, [handleNavigation, showLoader, hideLoader, pollForDeviceReady]);

	const handleRetry = () => {
		setError(null);
		setDeviceMessage('');
		setCurrentState(ViewState.PAY);
	};

	const handleCancel = () => {
		setError(null);
		handleNavigation('payment-method');
	};

	const handleServiceFeeConfirm = () => {
		logger.logGeneral.log({}, '[PaymentCardToken] Service fee confirmed, transitioning to PAY');
		hasConfirmedFeeRef.current = true;
		setCurrentState(ViewState.PAY);
	};

	const handleServiceFeeCancel = () => {
		logger.logGeneral.log({}, '[PaymentCardToken] Service fee cancelled, navigating to payment-method');
		handleNavigation('payment-method');
	};

	// Register/clear custom back action based on current view state
	useEffect(() => {
		if (currentState === ViewState.PAY || currentState === ViewState.PROCESSING) {
			setCustomBackAction(handleBack);
		} else {
			clearCustomBackAction();
		}
		return () => clearCustomBackAction();
	}, [currentState, handleBack, setCustomBackAction, clearCustomBackAction]);

	// --- View renderers ---

	const renderServiceFeeView = () => {
		if (!transactionFee || !transactionTotalWithFee) {
			return (
				<section className={style.processing}>
					<div className={style.processing__spinner}></div>
					<h2 className={style.processing__title}>Loading...</h2>
				</section>
			);
		}

		const feeValue = parseFloat(transactionFee || '0');

		return (
			<ServiceFeeView
				amount={cartTotalAmount}
				fee={feeValue}
				onConfirm={handleServiceFeeConfirm}
				onCancel={handleServiceFeeCancel}
				labels={
					paymentFeeViewServerProps
						? paymentFeeViewServerProps
						: ({ titleLabelKey: 'service-fee-description-card' } as PaymentFeeViewServerProps)
				}
			/>
		);
	};

	const renderPayView = () => (
		<section className={style.section}>
			<img className={style.section__image} src={cardInsertImage} alt={imageAltKey} />
			<BalancesKvpList
				className={style.section__details}
				kvpList={[{ label: getString(paymentTotalLabelKey ?? ''), value: parseFloat(transactionTotalWithFee || '0') }]}
			/>
			<MessageBoxSet
				className={style.section__messages}
				infoText={deviceMessage || getString('PLEASE WAIT')}
				warningText={getString(warningBoxLabelKey ?? '')}
			/>
		</section>
	);

	const renderErrorView = () => (
		<section className={style.error}>
			<MainMessageBox icon={AppIconType.Error} message={error?.message ?? getString(cardErrorGenericLabelKey)} />
			<Confirm
				titleLabel={getString(tryAgainMessageLabelKey)}
				yesButton={{ label: tryAgainYesLabelKey ?? getString('yes'), value: 'yes', target: '' }}
				noButton={{ label: tryAgainNoLabelKey ?? getString('no'), value: 'no', target: '' }}
				yesCallback={handleRetry}
				noCallback={handleCancel}
			/>
			{errorHint && <MessageBox type='warning' dangerouslySetInnerHTML={{ __html: errorHint }} />}
		</section>
	);

	const renderCurrentView = () => {
		if (hasConfirmedFeeRef.current && currentState !== ViewState.PROCESSING && currentState !== ViewState.ERROR) {
			logger.debug.log(`[PaymentCardToken] Rendering: PAY (fee confirmed, state=${currentState})`);
			return renderPayView();
		}

		logger.debug.log(`[PaymentCardToken] Rendering: ${currentState}`, {
			transactionFee,
			transactionTotalWithFee,
			hasConfirmedFee: hasConfirmedFeeRef.current,
		});

		switch (currentState) {
			case ViewState.SERVICE_FEE:
				return renderServiceFeeView();
			case ViewState.PAY:
				return renderPayView();
			case ViewState.ERROR:
				return renderErrorView();
			default:
				return renderServiceFeeView();
		}
	};

	return <section className={style.section}>{renderCurrentView()}</section>;
}

export default PaymentCardToken;
